import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';

const feedbackTypes = [
  { value: 'bug', label: 'Bug Report', description: "Something isn't working as expected", icon: '🐛', color: 'red' },
  { value: 'feature', label: 'Feature Request', description: 'Suggest a new feature or improvement', icon: '💡', color: 'yellow' },
  { value: 'general', label: 'General Feedback', description: 'Share your thoughts or suggestions', icon: '💬', color: 'blue' }
];

const Feedback = () => {
  const navigate = useNavigate();
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      e.target.value = '';
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPEG, GIF, or WebP)');
      e.target.value = '';
      return;
    }

    setScreenshot(file);
    setError('');
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!type) {
      setError('Please select a feedback type');
      return;
    }
    if (!description.trim() || description.trim().length < 10) {
      setError('Please provide a detailed description (at least 10 characters)');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('type', type);
      formData.append('description', description.trim());
      if (screenshot) {
        formData.append('screenshot', screenshot);
      }

      await axios.post('/api/feedback', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSuccess('Thank you for your feedback! We\'ll review it and get back to you if needed.');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error submitting feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Send Feedback</h1>
          <p className="text-sm text-gray-500 mt-1">Help us improve by reporting bugs, requesting features, or sharing general feedback</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">{error}</div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm">{success}</div>
        )}

        <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
          {/* Feedback Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Feedback Type</label>
            <div className="space-y-2">
              {feedbackTypes.map((ft) => (
                <label
                  key={ft.value}
                  className={`flex items-center p-3 rounded-lg border cursor-pointer transition-colors ${
                    type === ft.value
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="type"
                    value={ft.value}
                    checked={type === ft.value}
                    onChange={(e) => setType(e.target.value)}
                    className="mr-3 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="mr-2 text-lg">{ft.icon}</span>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{ft.label}</div>
                    <div className="text-xs text-gray-500">{ft.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              id="description"
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please provide detailed information about your feedback. For bug reports, include steps to reproduce the issue."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm resize-y min-h-[150px]"
              required
            />
            <p className="text-xs text-gray-500 mt-1">Be as specific as possible. The more details you provide, the better we can help.</p>
          </div>

          {/* Screenshot */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Screenshot (Optional)</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              onChange={handleScreenshotChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {preview && (
              <div className="mt-3 relative inline-block">
                <img src={preview} alt="Preview" className="max-w-xs max-h-48 rounded-lg border border-gray-300" />
                <button
                  type="button"
                  onClick={removeScreenshot}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 text-xs"
                >
                  ✕
                </button>
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">Supports PNG, JPEG, GIF, and WebP (max 5MB)</p>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              ← Back to Dashboard
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Send Feedback'}
            </button>
          </div>
        </form>

        {/* Tips */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Tips for Better Feedback</h3>
          <ul className="text-xs text-blue-700 space-y-1">
            <li>• <strong>Bug Reports:</strong> Include what you expected vs. what actually happened</li>
            <li>• <strong>Feature Requests:</strong> Explain the problem you're trying to solve</li>
            <li>• <strong>General Feedback:</strong> Share your overall experience and suggestions</li>
          </ul>
        </div>
      </div>
    </Layout>
  );
};

export default Feedback;
